import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Trash2, FileDown, Phone, MessageCircle } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";

export const STATUSES = ["Yeni", "İletişime Geçildi", "Teklif Verildi", "Tamamlandı", "İptal"];
const STATUS_CLS = { Yeni: "bg-red-100 text-red-800", "İletişime Geçildi": "bg-blue-100 text-blue-800", "Teklif Verildi": "bg-amber-100 text-amber-800", Tamamlandı: "bg-emerald-100 text-emerald-800", İptal: "bg-zinc-200 text-zinc-700" };
const CARD = { silahli: "Silahlı", silahsiz: "Silahsız", yok: "Yok" };
const EMAIL_ST = { sent: "Gönderildi", failed: "Gönderilemedi", partial: "Kısmen gönderildi", not_configured: "Adres tanımlı değil", pending: "Bekliyor" };

const CONFIG = {
  quotes: { title: "Teklif Talepleri", sub: (r) => `${r.company || "-"} · ${r.service_type}`, fields: [["company", "Firma / Site"], ["phone", "Telefon"], ["email", "E-posta"], ["city", "İl"], ["district", "İlçe"], ["service_type", "Hizmet Türü"], ["facility_type", "Tesis Türü"], ["staff_count", "Personel İhtiyacı"], ["shift", "Çalışma Düzeni"], ["start_date", "Başlama Tarihi"], ["message", "Açıklama"], ["marketing_consent", "Ticari İleti İzni"]] },
  applications: { title: "İş Başvuruları", sub: (r) => `${r.position} · ${CARD[r.card_type] || ""}`, fields: [["phone", "Telefon"], ["email", "E-posta"], ["birth_year", "Doğum Yılı"], ["city", "İl"], ["district", "İlçe"], ["position", "Pozisyon"], ["card_type", "Kimlik Kartı"], ["card_expiry", "Kart Geçerlilik"]] },
  messages: { title: "İletişim Mesajları", sub: (r) => r.subject || "-", fields: [["phone", "Telefon"], ["email", "E-posta"], ["subject", "Konu"], ["message", "Mesaj"]] },
};

function show(k, v) {
  if (k === "marketing_consent") return v ? "Evet" : "Hayır";
  if (k === "card_type") return CARD[v];
  return v || "-";
}

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error("İndirme başarısız");
  }
}

function DetailFields({ cfg, item }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {cfg.fields.map(([k, l]) => (
        <div key={k} className={k === "message" ? "sm:col-span-2" : ""}>
          <dt className="text-xs font-bold uppercase tracking-wider text-zinc-500">{l}</dt>
          <dd className="mt-0.5 whitespace-pre-wrap text-zinc-900">{show(k, item[k])}</dd>
        </div>
      ))}
    </dl>
  );
}

function ContactActions({ kind, item }) {
  const wa = `https://wa.me/${(item.phone || "").replace(/\D/g, "").replace(/^0/, "90")}`;
  return (
    <div className="flex flex-wrap gap-2">
      <a href={`tel:${item.phone}`} className="btn-outline !py-2"><Phone className="h-4 w-4" /> Ara</a>
      <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-outline !py-2"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
      {kind === "applications" && item.cv && (
        <button onClick={() => download(`/admin/applications/${item.id}/cv`, item.cv.filename)} className="btn-red !py-2" data-testid="cv-download-btn"><FileDown className="h-4 w-4" /> Özgeçmişi İndir</button>
      )}
    </div>
  );
}

function StatusAndNote({ item, patch, del }) {
  const [note, setNote] = useState(item.note || "");
  return (
    <>
      <div>
        <label className="field-label">Durum</label>
        <select defaultValue={item.status} onChange={(e) => patch({ status: e.target.value })} className="field-input" data-testid="detail-status-select">
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="field-label">İç not</label>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="field-input" data-testid="detail-note" />
        <div className="mt-2 flex justify-between">
          <button onClick={() => patch({ note })} className="btn-outline !py-2" data-testid="detail-note-save">Notu Kaydet</button>
          <button onClick={del} className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline" data-testid="detail-delete-btn"><Trash2 className="h-4 w-4" /> Sil</button>
        </div>
      </div>
    </>
  );
}

function Detail({ kind, item, onClose, onChanged }) {
  const patch = async (body) => {
    try {
      await api.patch(`/admin/submissions/${kind}/${item.id}`, body);
      toast.success("Kaydedildi");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm("Bu kayıt kalıcı olarak silinsin mi?")) return;
    await api.delete(`/admin/submissions/${kind}/${item.id}`);
    toast.success("Silindi");
    onClose();
    onChanged();
  };
  return (
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="submission-detail">
      <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>
      <p className="text-xs text-zinc-500">{fmtDate(item.created_at)} · E-posta bildirimi: {EMAIL_ST[item.email_status] || "-"}</p>
      <DetailFields cfg={CONFIG[kind]} item={item} />
      <ContactActions kind={kind} item={item} />
      <StatusAndNote item={item} patch={patch} del={del} />
    </DialogContent>
  );
}

function SubmissionTable({ rows, cfg, onOpen }) {
  if (!rows) return <Spinner />;
  if (rows.length === 0) return <p className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="submissions-empty">Kayıt bulunamadı.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid="submissions-table">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
          <tr><th className="px-4 py-3">Tarih</th><th className="px-4 py-3">Ad Soyad</th><th className="px-4 py-3">Detay</th><th className="px-4 py-3">Telefon</th><th className="px-4 py-3">Durum</th></tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r)} className="cursor-pointer transition-colors hover:bg-zinc-50" data-testid={`submission-row-${r.id}`}>
              <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
              <td className="px-4 py-3 font-semibold text-zinc-900">{r.name}</td>
              <td className="px-4 py-3 text-zinc-600">{cfg.sub(r)}</td>
              <td className="whitespace-nowrap px-4 py-3">{r.phone}</td>
              <td className="px-4 py-3"><span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLS[r.status]}`}>{r.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Submissions({ kind }) {
  const cfg = CONFIG[kind];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/submissions/${kind}`, { params: { q, status } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [kind, q, status]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <AdminTitle title={cfg.title} desc={rows ? `${rows.length} kayıt` : ""}>
        <button onClick={() => download(`/admin/submissions/${kind}/export.csv?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`, `${kind}.csv`)} className="btn-outline !py-2" data-testid="export-csv-btn">
          <Download className="h-4 w-4" /> Excel / CSV
        </button>
      </AdminTitle>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, telefon, e-posta, firma ara..." className="field-input !pl-9" data-testid="submissions-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="field-input sm:w-56" data-testid="submissions-status-filter">
          <option value="">Tüm durumlar</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <SubmissionTable rows={rows} cfg={cfg} onOpen={setSel} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        {sel && <Detail key={sel.id} kind={kind} item={sel} onClose={() => setSel(null)} onChanged={load} />}
      </Dialog>
    </div>
  );
}
